import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Brain,
  BookOpen,
  Users,
  Newspaper,
  Sparkles,
  Library,
  Radio,
  LineChart,
  ArrowRight,
} from 'lucide-react';

const PILLARS = [
  {
    icon: Brain,
    title: 'Neon Alpha AI',
    description:
      'AI market summaries, earnings digests, macro briefs, and watchlist ideation — every output appended with “Educational use only.”',
  },
  {
    icon: Library,
    title: 'Ebook Library',
    description:
      'Premium EPUB + PDF library with a Kindle-style reader, bookmarks, highlights, and progress tracking across every category.',
  },
  {
    icon: Newspaper,
    title: 'Premium Research',
    description:
      'Subscriber-only macro reports, ETF & stock educational breakdowns, archive access, and early-access deep dives.',
  },
  {
    icon: Radio,
    title: 'Educational Signals',
    description:
      'Real-time educational alerts delivered via Telegram. Commentary and research only — never a recommendation to trade.',
  },
  {
    icon: LineChart,
    title: 'Portfolio Education',
    description:
      'Live screener, AI strategy backtester, paper-trading playground, and a public marketplace of community strategies.',
  },
  {
    icon: Users,
    title: 'Private Community',
    description:
      'Gated Discord, Telegram, and Circle access · livestreams · Q&A sessions · networking with serious self-directed learners.',
  },
];

const STATS = [
  { label: 'Tracked Tickers', value: '70+' },
  { label: 'Strategies', value: '100+' },
  { label: 'AI Models', value: 'GPT‑4o · Sonnet' },
  { label: 'Live Data', value: '5 sources' },
];

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden">
      {/* Decorative neon glows */}
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(99,102,241,0.35) 0%, rgba(168,85,247,0.15) 35%, transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="absolute top-[40%] -left-32 w-[600px] h-[600px] rounded-full pointer-events-none opacity-30"
        style={{
          background:
            'radial-gradient(circle, rgba(34,211,238,0.25) 0%, transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="absolute top-[20%] -right-40 w-[700px] h-[700px] rounded-full pointer-events-none opacity-30"
        style={{
          background:
            'radial-gradient(circle, rgba(168,85,247,0.25) 0%, transparent 70%)',
        }}
      />
      {/* Subtle dot grid */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-sm">
              <Sparkles size={12} className="text-cyan-300" />
              <span className="text-xs tracking-widest text-white/70 uppercase">
                Now in private beta
              </span>
            </div>
          </div>

          <h1 className="text-center text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
            <span className="bg-gradient-to-r from-cyan-200 via-white to-violet-200 bg-clip-text text-transparent">
              Neon Alpha
            </span>
            <br />
            <span className="text-white/90">Investment Club</span>
          </h1>

          <p className="text-center mt-6 text-lg md:text-xl text-white/60 max-w-3xl mx-auto leading-relaxed">
            Research. Intelligence. Education. Community.
            <br className="hidden md:block" />
            <span className="text-white/45">
              A premium AI-powered learning ecosystem — Bloomberg-grade research,
              Kindle-style ebooks, Substack-style insights.
            </span>
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-gradient-to-r from-cyan-400 to-violet-500 hover:opacity-90 text-black font-semibold px-8 py-6 text-base rounded-full transition-all hover:scale-[1.02]"
              data-testid="landing-cta-join"
            >
              Become a Member
              <ArrowRight size={16} className="ml-1" />
            </Button>
            <Button
              onClick={() => navigate('/pro-access')}
              variant="outline"
              className="border-white/15 text-white hover:bg-white/5 px-8 py-6 text-base rounded-full bg-white/[0.02] backdrop-blur-sm"
              data-testid="landing-cta-pro-access"
            >
              Get Pro Access
            </Button>
          </div>

          <p className="text-center mt-4 text-xs text-white/40">
            Free to join · Pro access included with THE INCOME BLUEPRINT bundle
          </p>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm p-4 text-center"
              >
                <p className="text-xl md:text-2xl font-bold text-white">{s.value}</p>
                <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mt-1">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative px-4 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-3">
              Six pillars of the club
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Built for the self-directed{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
                learner
              </span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto mt-3">
              Each module is an educational layer — not a tipping service, not an investment fund.
              Read, learn, paper-trade, discuss.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="group relative rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm p-6 hover:bg-white/[0.05] hover:border-cyan-300/30 transition-all"
                  data-testid={`landing-pillar-${p.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-cyan-300" size={22} />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1.5">{p.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{p.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRO ACCESS — THE INCOME BLUEPRINT */}
      <section className="relative px-4 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-10 overflow-hidden">
            <div
              aria-hidden
              className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-40 pointer-events-none"
              style={{
                background:
                  'radial-gradient(circle, rgba(34,211,238,0.4) 0%, transparent 70%)',
              }}
            />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-cyan-300/80 mb-3">
                  Pro Access · The Income Blueprint
                </p>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
                  One bundle.{' '}
                  <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
                    Everything unlocked.
                  </span>
                </h2>
                <p className="text-white/55 leading-relaxed mb-5">
                  Pro access is included with THE INCOME BLUEPRINT — a digital product bundle of
                  wealth information plus full trading-app membership. Purchase it on our official
                  Linktree, and your account is manually upgraded to Pro within 24 hours.
                </p>
                <Button
                  onClick={() => navigate('/pro-access')}
                  className="bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-400 hover:to-violet-400 text-white font-semibold shadow-[0_0_25px_rgba(139,92,246,0.5)]"
                  data-testid="landing-blueprint-cta"
                >
                  How to get Pro Access
                  <ArrowRight size={16} className="ml-1.5" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['1 Bundle', 'Wealth Info + App'],
                  ['24 hrs', 'Manual Upgrade'],
                  ['Linktree', 'Official Store'],
                  ['Unlimited', 'AI Courses'],
                  ['30+', 'Screener Filters'],
                  ['Private', 'Community'],
                ].map(([v, l]) => (
                  <div
                    key={l}
                    className="rounded-xl bg-white/[0.04] border border-white/10 p-3 text-center"
                  >
                    <p className="text-sm md:text-base font-bold text-cyan-200">{v}</p>
                    <p className="text-[9px] uppercase tracking-wider text-white/40 mt-0.5">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PUBLICATIONS / CTA */}
      <section className="relative px-4 py-24">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 mb-5">
            <BookOpen className="text-cyan-300" size={24} />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            Join the club with{' '}
            <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">
              THE INCOME BLUEPRINT
            </span>
          </h2>
          <p className="text-white/55 mb-8 max-w-2xl mx-auto">
            Premium research, full ebook library, Kindle-style reader, gated Discord/Telegram
            community, member livestreams, AI market summaries. Buy the bundle once — your account
            is upgraded to Pro within 24 hours.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-cyan-400 to-violet-500 hover:opacity-90 text-black font-semibold px-10 py-6 text-base rounded-full transition-all hover:scale-[1.02]"
            data-testid="landing-cta-join-bottom"
          >
            Start your membership
            <ArrowRight size={16} className="ml-1" />
          </Button>
          <p className="mt-4 text-xs text-white/35">
            By joining you agree to our{' '}
            <Link to="/terms" className="text-cyan-300 hover:underline">
              Terms
            </Link>{' '}
            and acknowledge that Neon Alpha is an educational platform, not a hedge fund or
            investment advisor.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Landing;
